import { FC, useMemo, useRef } from 'react';
import TableGridItem from '../../UI/TableGridItem';
import { createColumnDefs, createMapWithTableRows, defaultColumnDefs } from '../helpers/utils';
import { createTableRows, ninetiethPercentileCellStyleRenderer } from '../../../utils/agGridUtils';
import { createWorkingHoursRowFilter } from '../../../utils/workingHoursUtils';
import type { Dayjs } from '@utils/dayjs';

interface ResultsCorrectionTabPanelProps {
    allDatesInAMonth: Dayjs[];
    correctedResults: any[];
    isLoading?: boolean;
    hideNonWorkingTime?: boolean;
    workingHours?: any[];
    publicHolidays?: any[];
}

const ResultsCorrectionTabPanel: FC<ResultsCorrectionTabPanelProps> = ({
    allDatesInAMonth,
    correctedResults,
    isLoading,
    hideNonWorkingTime = true,
    workingHours,
    publicHolidays,
}) => {
    const gridRef = useRef(null);

    const tableRowsCorrectedResults = useMemo(() => {
        return correctedResults.length > 0
            ? createTableRows(
                  createMapWithTableRows({
                      dates: allDatesInAMonth,
                      correctedResults,
                      workingHours,
                      publicHolidays,
                  }),
                  'slotTime'
              )
            : [];
    }, [correctedResults, allDatesInAMonth, workingHours, publicHolidays]);

    // Filter rows to hide non-working time slots if toggle is enabled
    const filteredRowData = useMemo(() => {
        const filterFn = createWorkingHoursRowFilter(allDatesInAMonth, workingHours, hideNonWorkingTime);
        return tableRowsCorrectedResults.filter(filterFn);
    }, [tableRowsCorrectedResults, hideNonWorkingTime, allDatesInAMonth, workingHours]);

    // Memoize column definitions to avoid re-creation on every render
    const columnDefs = useMemo(
        () =>
            createColumnDefs({
                allDatesInAMonth,
                valuePropertyName: 'value',
                showPercentageSign: true,
                cellStyleRenderer: ninetiethPercentileCellStyleRenderer,
            }),
        [allDatesInAMonth]
    );

    return (
        <TableGridItem
            ref={gridRef}
            rowData={filteredRowData}
            defaultColDef={defaultColumnDefs}
            columnDefs={columnDefs}
            columnHoverHighlight={true}
            loading={isLoading}
        />
    );
};

export default ResultsCorrectionTabPanel;
